import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Calendar, {
  CALENDAR_TYPE,
} from '@product-framework/components/Calendar';
import {
  DATE_FORMAT,
  FormErrors,
  LoadingEvent,
  useDataProvider,
} from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import DispatchHelper from '../../../lib/DispatchHelper';
import DateCellContent from '../components/DispatchCalendar/DateCellContent';
import DispatchNavigation from '../components/DispatchCalendar/DispatchNavigation';
import DispatchCalendarModal from '../components/DispatchCalendar/DispatchCalendarModal';
import RescheduleHelper from '../../../lib/RescheduleHelper';
import AssignReAssignHelper from '../../../lib/AssignReAssignHelper';
import { useServiceJob } from '../../../../../providers/ServiceJobProvider';

function PresetCalendar({
  serviceProviderId = null,
  excludeServiceProviderId = null,
  isRescheduleFlow = false,
  isReAssign = false,
  isAssign = false,
  locationId = null,
}) {
  const firstExcecutionDone = useRef(false);
  const { serviceJob } = useServiceJob();

  const [errors, setErrors] = useState(null);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);

  const [loading, setLoading] = useState(true);
  const [calendarData, setCalendarData] = useState({});
  const [monthInView, setMonthInView] = useState(() => dayjs().startOf('day'));
  const [selectedDayAndSlot, setSelectedDayAndSlot] = useState(null);
  const [timeSlotFilters, setTimeSlotFilters] = useState([]);

  const getHelper = () => {
    if (isRescheduleFlow) {
      return RescheduleHelper;
    }
    if (isAssign || isReAssign) {
      return AssignReAssignHelper;
    }
    return DispatchHelper;
  };

  const Helper = getHelper();

  const filteredCalendarData = useMemo(() => {
    if (!timeSlotFilters.length) {
      return calendarData;
    }

    return {
      ...calendarData,
      days: calendarData.days?.map(day => ({
        ...day,
        slots: day.slots?.filter(slot => timeSlotFilters.includes(slot.code)),
      })),
    };
  }, [calendarData, timeSlotFilters]);

  const fetchAndSetLiveCalendarData = async () => {
    setErrors([]);
    const fetchFromDate = dayjs(monthInView).format(DATE_FORMAT.CODE_FORMAT);
    const liveCalendarResponse = await Helper.fetchCalendarData({
      dated: fetchFromDate,
      serviceProviderId,
      excludeServiceProviderId,
      locationId,
      getApiUrl,
      serviceJob,
      get,
    });
    if (liveCalendarResponse?.ok) {
      setCalendarData(liveCalendarResponse?.data);
    } else {
      setErrors(liveCalendarResponse?.errors);
      setCalendarData({});
    }
  };

  const initialize = async () => {
    setLoading(true);
    await fetchAndSetLiveCalendarData();
    firstExcecutionDone.current = true;
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, [serviceJob?.id, serviceProviderId, excludeServiceProviderId]);

  useEffect(() => {
    if (firstExcecutionDone.current) {
      fetchAndSetLiveCalendarData();
    }
  }, [monthInView]);

  if (loading) {
    <LoadingEvent>
      <FormattedMessage
        id="FETCHING_CALENDAR_INFORMATION"
        defaultMessage="Fetching Calendar Information"
      />
    </LoadingEvent>;
  }

  return (
    <Box>
      {errors && (
        <Box>
          <FormErrors errors={errors || []} />
        </Box>
      )}
      <Calendar
        calendarType={CALENDAR_TYPE.PERIOD}
        isClickable={({ cellDate, currentDate }) => {
          const isPast = cellDate.isBefore(currentDate, 'day');
          return !isPast;
        }}
        // Information to be fed to each date cell
        getDateCellValue={({ value, textDate }) => {
          const dayData = value.days?.find(d => {
            return d.date === textDate;
          });
          return dayData;
        }}
        weeks={4}
        value={filteredCalendarData}
        onChange={data => setMonthInView(data)}
        DayCellContentComponent={{
          component: DateCellContent,
          componentProps: { setSelectedDayAndSlot, setErrors },
        }}
        NavigationComponent={{
          rightComponent: DispatchNavigation,
          rightComponentProps: {
            timeSlotFilters,
            setTimeSlotFilters,
          },
        }}
      />
      {selectedDayAndSlot && (
        <DispatchCalendarModal
          open={Boolean(selectedDayAndSlot)}
          onClose={() => setSelectedDayAndSlot(null)}
          value={selectedDayAndSlot}
          jobInfo={serviceJob}
          setErrors={setErrors}
          returnToJobPage={() => DispatchHelper.returnToJobPage(serviceJob)}
          isRescheduleFlow={isRescheduleFlow}
          preferredServiceProviderId={serviceProviderId}
          isReAssign={isReAssign}
          isAssign={isAssign}
          locationId={locationId}
        />
      )}
    </Box>
  );
}

export default PresetCalendar;
